import { type TokenStats } from '@/pages/api/tokens/info';
import { ReactNode } from 'react';

import { TextSkeleton } from '@/components/Skeleton';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { classNames } from '@/utils/classnames';
import { abbreviateNumber } from '@/utils/numbers';

interface StatConfig {
  key: keyof TokenStats;
  label: string;
  mobileHidden?: boolean;
  tooltip: { header: string; content: ReactNode; width?: string };
  formatter: (value: number) => string;
}

const getStatConfigs = (
  currencySymbol: string,
  convertFromUsd: (v: number) => number,
): StatConfig[] => [
  {
    key: 'marketCap',
    label: 'Market Cap',
    tooltip: {
      header: 'Market Cap',
      content: (
        <div className="block">
          <span style={{ fontWeight: '500' }}>Market Cap = </span> Current Price x Circulating
          Supply <br />
          <br /> Refers to the total market value of a token&apos;s circulating supply.
        </div>
      ),
      width: '240px',
    },
    formatter: (value) => `${currencySymbol}${abbreviateNumber(convertFromUsd(value), 2)}`,
  },
  {
    key: 'fdv',
    label: 'FDV',
    mobileHidden: true,
    tooltip: {
      header: 'Market Stats',
      content: (
        <div className="block">
          <span style={{ fontWeight: '500' }}>Fully Diluted Valuation (FDV) = </span>
          Current Price x Total Supply <br />
          <br />
          FDV is the theoretical market capitalization of a token if the entirety of its supply is
          in circulation, based on its current market price.
        </div>
      ),
      width: '241px',
    },
    formatter: (value) => `${currencySymbol}${abbreviateNumber(convertFromUsd(value), 1)}`,
  },
  {
    key: 'volume24h',
    label: 'Volume (24h)',
    tooltip: {
      header: 'Market Stats',
      content:
        'The total trading volume of a token across all monitored platforms over the past 24 hours.',
      width: '231px',
    },
    formatter: (value) => `${currencySymbol}${abbreviateNumber(convertFromUsd(value), 1)}`,
  },
  {
    key: 'holders',
    label: 'Holders',
    tooltip: {
      header: 'Market Stats',
      content: 'The total number of unique wallet addresses that own the token.',
      width: '223px',
    },
    formatter: (value) => abbreviateNumber(value, 1, true),
  },
];

interface Props {
  isLoading: boolean;
  tokenStats?: TokenStats | null;
}

export const TokenProfileMarketStats = ({ isLoading, tokenStats }: Props) => {
  const { currencySymbol, convertFromUsd } = useFiatDisplay();

  const stats = getStatConfigs(currencySymbol, convertFromUsd);

  const getStatValue = (stat: StatConfig) => {
    const value = tokenStats?.[stat.key];
    return value != null && !Number.isNaN(value) ? stat.formatter(value) : '-';
  };

  return (
    <ul className="flex flex-row mt-3 border-t border-token-gray-300">
      {stats.map((stat) => (
        <li
          className={classNames(
            'px-1 pt-3 not-first:pl-3 flex-1 not-last:border-r border-token-gray-300',
            '[border-image:linear-gradient(180deg,transparent_30%,var(--token-color-gray-300)_40%_80%,transparent_90%)_1]',
            { 'hidden tablet:block': stat.mobileHidden },
          )}
          key={stat.key}
        >
          <div
            className={fontStyles({
              variant: 'LABEL_XS',
              class: 'text-token-gray-800 h-4 items-center',
            })}
          >
            {stat.label}{' '}
            <MatchaTooltip delayDuration={0}>
              <MatchaTooltipTrigger>
                <button className="cursor-default">
                  <InfoIconOutline className="w-3 h-3 color-[rgba(146, 153, 166, 1)] -mb-0.5" />
                </button>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>{stat.tooltip.content}</MatchaTooltipContent>
              <MatchaTooltipPopover
                className={typeof stat.tooltip.content === 'string' ? undefined : 'py-3'}
                style={stat.tooltip.width != null ? { width: stat.tooltip.width } : undefined}
                sideOffset={5}
              />
              <MatchaTooltipBottomSheet header={stat.tooltip.header} />
            </MatchaTooltip>
          </div>
          {isLoading && <TextSkeleton height={16} width={48} className="my-1" />}
          {!isLoading && (
            <h5
              className={fontStyles({
                variant: 'LABEL_LG',
                class: ['items-center', 'flex', 'font-[1.25rem]', 'leading-6'],
              })}
            >
              {getStatValue(stat)}
            </h5>
          )}
        </li>
      ))}
    </ul>
  );
};
